import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, message, Typography, Layout, Space, Breadcrumb, Tabs, Button, Avatar, Badge, Tag, Row, Col, Divider, Tooltip, Popover, Grid } from 'antd';
import {
    ApiOutlined,
    ArrowLeftOutlined,
    SyncOutlined,
    StopOutlined,
    RobotOutlined,
    KeyOutlined,
    WhatsAppOutlined,
    FacebookOutlined,
    MessageOutlined,
    InstagramOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    SafetyCertificateOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';
import { getChannelById } from '../ChannelsServices';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import ContactsTab from './ContactsTab/ContactsTab';
import HistoricalImportsTab from './HistoricalImportsTab/HistoricalImportsTab';
import SettingsTab from './SettingsTab/SettingsTab';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const getPlatformIcon = (channelType) => {
    switch (channelType?.toLowerCase()) {
        case 'whatsapp':
            return WhatsAppOutlined;
        case 'messenger':
            return MessageOutlined;
        case 'facebook_page':
            return FacebookOutlined;
        case 'instagram':
            return InstagramOutlined;
        default:
            return MessageOutlined;
    }
};

const getPlatformColor = (channelType) => {
    switch (channelType?.toLowerCase()) {
        case 'whatsapp':
            return '#25D366';
        case 'messenger':
        case 'facebook_page':
            return '#1877F2';
        case 'instagram':
            return '#E4405F';
        default:
            return '#888';
    }
};

const ChannelDetail = () => {
    const { t } = useTranslation();
    const { channelId } = useParams();
    const navigate = useNavigate();
    const { setLoading } = useGlobal();
    const { handleError } = useErrorHandler();
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint();
    const [channel, setChannel] = useState(null);
    const [activeTab, setActiveTab] = useState('contacts');

    const fetchChannel = useCallback(async () => {
        if (!channelId) {
            message.error(t('no_channel_id_provided'));
            return;
        }
        try {
            setLoading(true);
            const response = await getChannelById(channelId);
            setChannel(response.data);
        } catch (error) {
            handleError(error);
            navigate('/channels');
        } finally {
            setLoading(false);
        }
    }, [channelId, setLoading, handleError, navigate, t]);

    useEffect(() => {
        if (channelId) {
            fetchChannel();
        }
    }, [channelId, fetchChannel]);

    if (!channelId) {
        return (
            <Content>
                <Card>
                    <Text type="danger">{t('no_channel_id_provided')}</Text>
                </Card>
            </Content>
        );
    }

    if (!channel) {
        return null;
    }

    const PlatformIcon = getPlatformIcon(channel.channel_type);
    const platformColor = getPlatformColor(channel.channel_type);
    const isMobile = !screens.md;

    const tabItems = [
        {
            key: 'contacts',
            label: 'Contacts',
            children: <ContactsTab channel={channel} onUpdate={fetchChannel} />
        },
        {
            key: 'historical-imports',
            label: 'Historical Imports',
            children: <HistoricalImportsTab channel={channel} />
        },
        {
            key: 'settings',
            label: 'Settings',
            children: <SettingsTab channel={channel} onUpdate={fetchChannel} />
        },
    ];

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        message.success(t('copied_to_clipboard'));
    };

    // Helper to render token status
    const renderTokenStatus = () => {
        if (channel.is_permanent_token) {
            return { icon: <SafetyCertificateOutlined />, text: t('permanent_token'), color: isDarkMode ? '#49aa19' : '#52c41a' };
        }
        if (channel.token_expires_at) {
            const expiry = new Date(channel.token_expires_at);
            const daysLeft = Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
            if (daysLeft < 0) return { icon: <ExclamationCircleOutlined />, text: t('expired'), color: '#ff4d4f' };
            if (daysLeft < 7) return { icon: <ClockCircleOutlined />, text: `${t('expires_in')} ${daysLeft} ${t('days')}`, color: '#faad14' };
            return { icon: <CheckCircleOutlined />, text: t('valid'), color: isDarkMode ? '#177ddc' : '#1890ff' };
        }
        return { icon: <InfoCircleOutlined />, text: t('unknown'), color: 'default' };
    };

    const tokenStatus = renderTokenStatus();

    const permissionsContent = (
        <div style={{ maxWidth: 300 }}>
            <Paragraph style={{ marginBottom: 8 }} strong>{t('granted_permissions')}:</Paragraph>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {channel.permissions_json?.scopes?.map(scope => (
                    <Tag key={scope} style={{ margin: 0 }}>{scope}</Tag>
                )) || <Text type="secondary">{t('no_permissions_info')}</Text>}
            </div>
        </div>
    );

    return (
        <Content>
            {/* Breadcrumb */}
            <div style={{ marginBottom: '24px' }}>
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/channels">
                                <ApiOutlined /> {isMobile ? '' : t('channels')}
                            </Link>,
                        },
                        {
                            title: channel.name || channel.external_channel_id,
                        },
                    ]}
                />
            </div>

            {/* Header Card */}
            <Card
                style={{
                    marginBottom: '24px',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.05)'
                }}
                bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* Top Row: Back Button & Main Info */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/channels')}
                            type="text"
                            style={{ padding: 0, height: 'auto', marginTop: isMobile ? '4px' : '8px' }}
                        />

                        <div style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            gap: isMobile ? '16px' : '24px',
                            flex: 1
                        }}>
                            <Badge
                                count={
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: isMobile ? 24 : 28,
                                        height: isMobile ? 24 : 28,
                                        borderRadius: '50%',
                                        backgroundColor: platformColor,
                                        border: `3px solid ${isDarkMode ? '#141414' : '#fff'}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        <PlatformIcon style={{ fontSize: isMobile ? 12 : 14, color: 'white' }} />
                                    </div>
                                }
                                offset={isMobile ? [-5, 45] : [-5, 55]}
                            >
                                <Avatar
                                    size={isMobile ? 64 : 72}
                                    shape="square"
                                    src={channel.picture_url}
                                    icon={<PlatformIcon style={{ fontSize: 32 }} />}
                                    style={{
                                        backgroundColor: channel.picture_url ? 'transparent' : `${platformColor}15`,
                                        color: platformColor,
                                        borderRadius: '16px',
                                    }}
                                />
                            </Badge>

                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                    <Title level={2} style={{
                                        margin: 0,
                                        fontSize: isMobile ? '20px' : '24px',
                                        lineHeight: 1.3,
                                        wordBreak: 'break-word'
                                    }}>
                                        {channel.name || channel.external_channel_id}
                                    </Title>
                                    <Tag
                                        bordered={false}
                                        color={channel.is_synchronization_enabled ? 'success' : 'default'}
                                        style={{
                                            margin: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            borderRadius: '100px',
                                            padding: '0 10px',
                                            fontSize: '12px',
                                            fontWeight: 500
                                        }}
                                    >
                                        {channel.is_synchronization_enabled ? (
                                            <><SyncOutlined /> {t('synced')}</>
                                        ) : (
                                            <><StopOutlined /> {t('not_synced')}</>
                                        )}
                                    </Tag>
                                </div>

                                <Space
                                    split={<Text type="secondary" style={{ fontSize: '10px' }}>•</Text>}
                                    wrap
                                    size={isMobile ? [0, 4] : 8}
                                    style={{ width: '100%' }}
                                    direction={isMobile ? 'vertical' : 'horizontal'}
                                >
                                    <Text type="secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <PlatformIcon style={{ color: platformColor }} />
                                        {t(channel.channel_type?.toLowerCase())}
                                    </Text>

                                    {channel.channel_json?.category && (
                                        <Text type="secondary">{channel.channel_json.category}</Text>
                                    )}

                                    <Text type="secondary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        {t('connected_on')} {format(new Date(channel.created_at), 'PPP')}
                                    </Text>
                                </Space>
                            </div>
                        </div>
                    </div>

                    <Divider style={{ margin: '0' }} />

                    {/* Meta Info Row - Flat Design */}
                    <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        padding: '12px 0',
                    }}>
                        {/* Agent Info */}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '12px 0' : '0 24px 0 0' }}>
                            <Avatar shape="square" size={40} icon={<RobotOutlined />} style={{ backgroundColor: isDarkMode ? '#1f1f1f' : '#f9f9f9', color: isDarkMode ? '#8c8c8c' : '#595959', borderRadius: '10px' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{t('linked_agent')}</Text>
                                <Text strong>{channel.agent ? channel.agent.name : t('no_agent')}</Text>
                            </div>
                        </div>

                        {!isMobile && <Divider type="vertical" style={{ height: 'auto', minHeight: '40px', margin: 'auto 0' }} />}

                        {/* Credential */}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '12px 0' : '0 24px' }}>
                            <Avatar shape="square" size={40} icon={<KeyOutlined />} style={{ backgroundColor: isDarkMode ? '#1f1f1f' : '#f9f9f9', color: isDarkMode ? '#8c8c8c' : '#595959', borderRadius: '10px' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{t('credential')}</Text>
                                <Text strong ellipsis style={{ maxWidth: 150 }}>{channel.credential ? channel.credential.name : '-'}</Text>
                            </div>
                        </div>

                        {!isMobile && <Divider type="vertical" style={{ height: 'auto', minHeight: '40px', margin: 'auto 0' }} />}

                        {/* Token Status */}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '12px 0' : '0 24px' }}>
                            <Avatar shape="square" size={40} icon={tokenStatus.icon} style={{ backgroundColor: isDarkMode ? '#1f1f1f' : '#f9f9f9', color: tokenStatus.color, borderRadius: '10px' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{t('token_status')}</Text>
                                <Text strong style={{ color: tokenStatus.color }}>{tokenStatus.text}</Text>
                            </div>
                        </div>

                        {!isMobile && <Divider type="vertical" style={{ height: 'auto', minHeight: '40px', margin: 'auto 0' }} />}

                        {/* Page ID */}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '12px 0' : '0 0 0 24px' }}>
                            <Avatar shape="square" size={40} icon={<InfoCircleOutlined />} style={{ backgroundColor: isDarkMode ? '#1f1f1f' : '#f9f9f9', color: isDarkMode ? '#8c8c8c' : '#595959', borderRadius: '10px' }} />
                            <div style={{ overflow: 'hidden' }}>
                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{t('channel_id')}</Text>
                                <Space size={4} style={{ width: '100%' }}>
                                    <Paragraph copyable={{ text: channel.external_channel_id, tooltips: false }} style={{ marginBottom: 0, width: '100%' }}>
                                        <Text strong ellipsis style={{ maxWidth: '100%' }}>{channel.external_channel_id}</Text>
                                    </Paragraph>
                                </Space>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Tabs */}
            <Card
                style={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.05)'
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    size="large"
                    tabBarStyle={{ marginBottom: '24px' }}
                />
            </Card>
        </Content>
    );
};

export default ChannelDetail;
